import { useState, useCallback, useRef, useEffect } from 'react';
import { invoke } from '@tauri-apps/api/core';

interface NdiFrameData {
  width: number;
  height: number;
  data: Uint8Array;
}

interface NdiStartOptions {
  sender_name: string;
  width: number;
  height: number;
}

export function useNdi() {
  const [isAvailable, setIsAvailable] = useState<boolean>(false);
  const [isActive, setIsActive] = useState<boolean>(false);
  const [senderName, setSenderName] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const isSendingRef = useRef<boolean>(false);
  // Pipeline IPC: autoriser plusieurs frames en vol pour masquer la latence
  // de transfert. Le worker Rust (channel capacité 1) drop les frames obsolètes.
  const inFlightCountRef = useRef<number>(0);
  const MAX_IN_FLIGHT = 4;

  // Vérifier si NDI est disponible au montage
  useEffect(() => {
    checkAvailability();
  }, []);

  const checkAvailability = useCallback(async () => {
    try {
      const available = await invoke<boolean>('ndi_is_available');
      setIsAvailable(available);
      if (!available) {
        setError('NDI Runtime introuvable. Installez NDI Runtime/SDK et vérifiez que la bibliothèque est dans le PATH.');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      setError(errorMessage);
      setIsAvailable(false);
    }
  }, []);

  const startSender = useCallback(async (options: NdiStartOptions) => {
    try {
      setError(null);
      // Initialiser la shared memory pour le transfert zéro-copie
      await invoke('ndi_init_shared_memory', {
        width: options.width,
        height: options.height,
      });
      await invoke('ndi_start_sender', { options });
      setIsActive(true);
      setSenderName(options.sender_name);
      isSendingRef.current = true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      setError(errorMessage);
      console.error('Erreur lors du démarrage du sender NDI:', err);
      throw err;
    }
  }, []);

  const stopSender = useCallback(async () => {
    try {
      setError(null);
      await invoke('ndi_stop_sender');
      await invoke('ndi_cleanup_shared_memory');
      setIsActive(false);
      setSenderName('');
      isSendingRef.current = false;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      setError(errorMessage);
      console.error('Erreur lors de l\'arrêt du sender NDI:', err);
      throw err;
    }
  }, []);

  // Indique si la boucle d'envoi peut capturer/envoyer une nouvelle frame
  // (évite un getImageData gaspillé quand le pipeline est saturé).
  const canSendFrame = useCallback(() => {
    return isSendingRef.current && inFlightCountRef.current < MAX_IN_FLIGHT;
  }, []);

  const sendFrame = useCallback(async (frameData: NdiFrameData) => {
    if (!isSendingRef.current || inFlightCountRef.current >= MAX_IN_FLIGHT) {
      return;
    }

    try {
      inFlightCountRef.current += 1;
      // Écrire directement dans la shared memory (zéro-copie)
      await invoke('ndi_write_shared_frame', frameData.data, {
        headers: {
          width: String(frameData.width),
          height: String(frameData.height),
        },
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      setError(errorMessage);
      console.error('Erreur lors de l\'envoi de frame NDI:', err);
      // En cas d'erreur, on arrête l'envoi
      isSendingRef.current = false;
      setIsActive(false);
    } finally {
      inFlightCountRef.current -= 1;
    }
  }, []);

  const toggleSender = useCallback(async (enabled: boolean, config?: { width: number; height: number }) => {
    if (enabled && config) {
      await startSender({
        sender_name: 'VisualGrid',
        width: config.width,
        height: config.height,
      });
    } else {
      await stopSender();
    }
  }, [startSender, stopSender]);

  return {
    isAvailable,
    isActive,
    senderName,
    error,
    startSender,
    stopSender,
    sendFrame,
    canSendFrame,
    toggleSender,
    checkAvailability,
  };
}
